import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { UserAutocompleteResult } from './autocomplete/useUsersAutocomplete';
import type { BaseQuery } from './baseQuery.types';

export type BudgetGroup = components['schemas']['GetBudgetGroupsEndpoint_BudgetResponse'];
type GetBudgetGroupsResponse = components['schemas']['GetBudgetGroupsEndpoint_BudgetPagedResponse'];

export interface UseBudgetGroupsReturn extends BaseQuery {
  budgetGroups: GetBudgetGroupsResponse | null;
}

export function useBudgetGroups(
  page: number = 1,
  pageSize: number = 50,
  sortedBy: string | null = 'name',
  groupNames: BudgetGroup[] | null = null,
  emails: UserAutocompleteResult[] | null = null,
): UseBudgetGroupsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getBudgetGroups = async (
    organizationId: number,
    page: number,
    pageSize: number,
    sortedBy: string | null,
    groupNames: string[] | null,
    emails: string[] | null,
  ) => {
    if (organizationId === 0) {
      return {
        data: [],
        page: 1,
        pageSize,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      } as GetBudgetGroupsResponse;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.POST('/api/organizations/{organizationId}/budget-groups', {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
      body: {
        groupNames: groupNames && groupNames.length > 0 ? groupNames : null,
        usersEmailFilter: emails && emails.length > 0 ? emails : null,
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetBudgetGroupsResponse;
  };

  const extractedGroupNames: string[] | null =
    groupNames && groupNames.length > 0 ? groupNames.map((g) => g.name).filter((name): name is string => typeof name === 'string') : null;

  const extractedEmails: string[] | null =
    emails && emails.length > 0 ? emails.map((u) => u.email).filter((email): email is string => typeof email === 'string') : null;

  const useBudgetGroupsQuery = useQuery({
    queryKey: ['budgetGroups', organizationId, page, pageSize, sortedBy, groupNames, emails],
    queryFn: () => getBudgetGroups(organizationId, page, pageSize, sortedBy, extractedGroupNames, extractedEmails),
    enabled: organizationId > 0,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useBudgetGroupsQuery;

  return {
    budgetGroups: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
